<template>
  <div class="post-card">
    <div class="card-header">
      <img src="../assets/logo.png" alt="OSACE Logo" class="avatar" />
      <div class="header-info">
        <span class="creator-name">{{ post.creator_name }}</span>
        <span class="timestamp">{{ formatDate(post.created_at) }}</span>
      </div>
      <div v-if="userRole === 'admin' || userRole === 'coordonator'" class="admin-actions">
        <button @click="handleDelete" class="action-btn-admin delete-btn">🗑️</button>
      </div>
    </div>

    <div class="image-container" v-if="post.image_urls && post.image_urls.length > 0">
      <img :src="post.image_urls[0]" alt="Postare" class="post-image" />
    </div>

    <div class="actions-container">
      <button @click="handleLike" :class="['action-btn', { liked: isLiked }]">
        {{ isLiked ? '❤️' : '🤍' }}
      </button>
      </div>

    <div class="details-container">
      <span class="like-count">{{ likeCount }} like-uri</span>
      
      <span class="creator-name-desc">{{ post.creator_name }}</span>
      
      <p v-if="post.description" class="description">
        {{ isLongText && !isExpanded ? truncatedDescription : post.description }}
        
        <button v-if="isLongText" @click="toggleText" class="show-more-btn">
          {{ isExpanded ? 'Afișează mai puțin' : 'Afișează mai mult' }}
        </button>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import apiClient from '@/api.js';

// --- Props & Emits ---
// Primim 'post' de la părinte (din NewsFeed.vue)
const props = defineProps({
  post: {
    type: Object,
    required: true,
  }
});
const emit = defineEmits(['post-deleted']);

// --- Date ---
const MAX_DESCRIPTION_LENGTH = 100;
const userRole = ref('');

// Stare locală pentru Like (reacție instantanee)
const isLiked = ref(props.post.is_liked_by_me);
const likeCount = ref(parseInt(props.post.likes_count, 10));

// Stare locală pentru "Show More"
const isExpanded = ref(false);

// --- Computed Properties ---
const isLongText = computed(() => props.post.description?.length > MAX_DESCRIPTION_LENGTH);
const truncatedDescription = computed(() => {
  return props.post.description?.substring(0, MAX_DESCRIPTION_LENGTH) + '...';
});

// --- Logică ---
onMounted(() => {
  // Preluăm rolul user-ului pentru a afișa butoanele de admin
  try {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      userRole.value = userData.role;
    }
  } catch (e) {
    console.error("Eroare la citirea rolului în PostCard:", e);
  }
});

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const toggleText = () => {
  isExpanded.value = !isExpanded.value;
};

// --- API Calls ---
const handleLike = async () => {
  // Actualizăm UI-ul local instantaneu
  const newLikeState = !isLiked.value;
  const newLikeCount = newLikeState ? likeCount.value + 1 : likeCount.value - 1;
  isLiked.value = newLikeState;
  likeCount.value = newLikeCount;

  // Trimitem cererea la API în fundal
  try {
    if (newLikeState) {
      await apiClient.post(`/posts/${props.post.id}/like`);
    } else {
      await apiClient.delete(`/posts/${props.post.id}/like`);
    }
  } catch (error) {
    console.error("Eroare la acțiunea de like:", error);
    // Dacă API-ul eșuează, revenim la starea inițială
    isLiked.value = !newLikeState;
    likeCount.value = newLikeState ? newLikeCount - 1 : newLikeCount + 1;
    alert("Acțiunea de like a eșuat. Încearcă din nou.");
  }
};

const handleDelete = async () => {
  if (!window.confirm("Ești sigur că vrei să ștergi această postare?")) {
    return;
  }
  try {
    await apiClient.delete(`/posts/${props.post.id}`);
    // Anunțăm părintele (NewsFeed.vue) că postarea a fost ștearsă
    emit('post-deleted', props.post.id);
  } catch (error) {
    console.error("Eroare la ștergerea postării:", error);
    alert("Ștergerea a eșuat.");
  }
};
</script>

<style scoped>
.post-card {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 20px;
  max-width: 600px; /* Sau o lățime potrivită pentru feed-ul tău */
  margin-left: auto;
  margin-right: auto;
}
.card-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
}
.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 12px;
  background-color: #eee;
}
.header-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1; /* Face ca butoanele de admin să fie împinse la dreapta */
}
.creator-name {
  font-weight: bold;
  font-size: 1.1em;
}
.timestamp {
  font-size: 0.8em;
  color: #777;
}
.admin-actions {
  display: flex;
  gap: 8px;
}
.action-btn-admin {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.2em;
}
.delete-btn { color: #C0392B; }

.image-container {
  width: 100%;
  /* Păstrăm un aspect ratio pătrat, ca pe mobil */
  aspect-ratio: 1 / 1; 
  background-color: #eee;
}
.post-image {
  width: 100%;
  height: 100%;
  object-fit: cover; /* 'contain' dacă vrei să se vadă toată, 'cover' dacă vrei să umple spațiul */
}

.actions-container {
  display: flex;
  padding: 8px 16px;
  gap: 12px;
}
.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.8em; /* Mărim emoji-urile */
  padding: 0;
}
.action-btn.liked {
  /* Stil pentru când e 'liked' (deja e roșu de la emoji) */
}

.details-container {
  padding: 0 16px 16px 16px;
}
.like-count {
  display: block;
  font-weight: bold;
  margin-bottom: 8px;
}
.creator-name-desc {
  font-weight: bold;
  margin-right: 5px;
}
.description {
  display: inline; /* Face ca 'Show More' să vină lângă */
  margin: 0;
  line-height: 1.5;
}
.show-more-btn {
  background: none;
  border: none;
  color: #777;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  margin-left: 5px;
}
</style>